import { defineStore } from 'pinia'
import { invoke } from '~/lib/tauriInvoke'
import type { Group, GroupDetails, Category, Channel, Invite, GroupUser, Role, ChannelPermissionOverwrite, CategoryPermissionOverwrite, BannedGroupUser } from '~/composables/useGroups'
import * as Sentry from '@sentry/nuxt'
import { ChannelType } from '~/composables/useGroups'
import { liveKitManager } from '~/lib/livekit/manager'
import { voiceConnectionCoordinator } from '~/lib/livekit/voiceConnection'
import type { VideoQuality, ScreenShareQuality } from '~/lib/livekit/types'
import type { ConnectionQuality } from 'livekit-client'
import { SPEAKING_ATTRIBUTE_KEY } from '~/lib/livekit/speakingAttributePublisher'
import { DEAFENED_ATTRIBUTE_KEY } from '~/lib/livekit/deafenedAttributePublisher'
import { dualCaseString, mapRawChannelUser } from '~/lib/signalr/payload'
import { runAction } from '~/stores/storeErrorHandling'
import { effectivePermissionMask, maskHasFlag, PermissionFlag } from '~/lib/permissions'
import { useUserStore } from '~/stores/user'
import { isServiceUnavailableError } from '~/lib/platform/retry'
import { useNotificationSettingsStore } from '~/stores/notificationSettings'
import { MuteDuration, NotificationTargetType } from '~/lib/notifications/notificationPolicy'
import type { Subscription } from '~/types/user'

// Explicit page sizes for the paginated group lists (server maximums: 100 for users, 500 for
// invites/bans). Roles are deliberately requested without one — see `getGroupRoles`.
const GROUP_USERS_PAGE_SIZE = 100
const GROUP_INVITES_PAGE_SIZE = 100
const GROUP_BANS_PAGE_SIZE = 100

export interface GroupUsersPage {
  users: GroupUser[]
  next_page_token: string
}

export interface GroupInvitesPage {
  invites: Invite[]
  next_page_token: string
}

export interface GroupBansPage {
  bans: BannedGroupUser[]
  next_page_token: string
}

// Rules shared by every paginated list here (users, invites, bans):
// - The list ends only when `next_page_token` comes back empty. A page can be shorter than the
//   requested size — even empty — and still have a successor (the server drops deleted records
//   after selecting the page), so page length is never a signal.
// - The token is opaque and bound to its group + method. After the list itself changes (invite
//   created, ban lifted, ...) a held token must not be continued from: start over at page one.
// - A rejected token (`FAILED_PRECONDITION`, "Invalid page token.") means the same: reset the
//   list and load the first page.
export function isInvalidPageTokenError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return /invalid page token/i.test(message)
}

// Upserts `incoming` into `existing` keyed by user id. Pages overlap by design: the first page of
// GetGroupUsers always carries the current user (even if their natural position is further down,
// so it can hold page_size + 1 entries), and the same member then shows up again on their own
// page — appending pages as-is would duplicate them.
function mergeGroupUsers(existing: GroupUser[], incoming: GroupUser[]): GroupUser[] {
  const byId = new Map(existing.map((user) => [user.id.toLowerCase(), user]))
  for (const user of incoming) byId.set(user.id.toLowerCase(), user)
  return [...byId.values()]
}

// A create/join command is never retried automatically (see `~/lib/platform/retry.ts`): when it
// fails with a timeout/unavailable error the request may still have been applied. The caller shows
// the error; this re-reads the affected list so the UI reflects what actually happened before the
// user decides whether to try again.
function rereadAfterUncertainWrite(error: unknown, reread: () => unknown) {
  if (!isServiceUnavailableError(error)) return
  Promise.resolve()
    .then(reread)
    .catch(() => {
      // best-effort — the re-read reports its own failure
    })
}

// Computes the current user's effective permission bitmask for a group: every permission granted
// by any role they're assigned (OR'd together), or every permission at once if they're the group
// owner or hold the Administrator flag on any role (see `effectivePermissionMask`). Takes `state`
// explicitly (Pinia getters here are plain arrow functions, so there's no store `this` to read
// other getters/state off of) — this is what `groupPermissionMask`/`hasGroupPermission` below
// build on, and it's what every "is this owner-only?" UI check should now go through instead of
// `isGroupOwner`.
function computeGroupPermissionMask(state: { groupDetails: Record<string, GroupDetails>, groupRoles: Record<string, Role[]>, groupUsers: Record<string, GroupUser[]> }, groupId: string): bigint {
  const isOwner = !!state.groupDetails[groupId]?.i_am_is_owner
  if (isOwner) return effectivePermissionMask(undefined, undefined, true)
  const currentUserId = useUserStore().user?.id
  if (!currentUserId) return 0n
  const member = state.groupUsers[groupId]?.find((u) => u.id.toLowerCase() === currentUserId.toLowerCase())
  return effectivePermissionMask(state.groupRoles[groupId], member?.role_ids, false)
}

// The `speaking` attribute (see `speakingAttributePublisher.ts`) is driven by a zero-latency
// Web Audio analyser and round-trips through the server just like any other LiveKit attribute,
// so prefer it once a participant has ever sent one — it reacts faster and more consistently
// than LiveKit's own polled `Participant.isSpeaking`. Fall back to `isSpeaking` for anyone who
// hasn't sent the attribute yet (e.g. the very first moment after joining, or a client/token
// that can't set attributes at all — see the `canUpdateOwnMetadata` grant requirement).
function resolveIsSpeaking(p: { isSpeaking?: boolean; attributes?: Readonly<Record<string, string>> }): boolean {
  const attr = p.attributes?.[SPEAKING_ATTRIBUTE_KEY]
  if (attr === 'true') return true
  if (attr === 'false') return false
  return p.isSpeaking ?? false
}

// No local LiveKit-SDK signal exists for "this remote participant turned off their audio
// output" (unlike mic/camera, which are real published tracks) — it's purely self-reported via
// the `deafened` attribute (see `deafenedAttributePublisher.ts`), so absence of the attribute
// (participant on an older client, or attribute not yet propagated) must default to `false`.
function resolveIsDeafened(p: { attributes?: Readonly<Record<string, string>> }): boolean {
  return p.attributes?.[DEAFENED_ATTRIBUTE_KEY] === 'true'
}

export interface Participant {
  sid: string
  identity: string
  name: string
  is_local: boolean
  joined_at: number
  isSpeaking?: boolean
  isVideoEnabled?: boolean
  isAudioEnabled?: boolean
  isScreenShareEnabled?: boolean
  isHandRaised?: boolean
  isDeafened?: boolean
  connectionQuality?: ConnectionQuality
}

type UserMetadata = {
  avatarUrl?: string
  bannerUrl?: string
  description?: string
  displayName?: string
  status?: string
  /** `undefined` (or NaN) — the event didn't carry it, keep what we have. */
  presenceStatus?: number
  lastActivity?: string
}

function updateUserData(targetUser: GroupUser, metadata: UserMetadata) {
  if (metadata.avatarUrl !== undefined) targetUser.avatar_url = metadata.avatarUrl
  if (metadata.bannerUrl !== undefined) targetUser.banner_url = metadata.bannerUrl
  if (metadata.description !== undefined) targetUser.description = metadata.description
  if (metadata.displayName !== undefined) targetUser.display_name = metadata.displayName
  if (metadata.status !== undefined) targetUser.status = metadata.status

  if (metadata.presenceStatus !== undefined && !Number.isNaN(metadata.presenceStatus)) {
    targetUser.user_status = metadata.presenceStatus
  }
  if (metadata.lastActivity) targetUser.last_activity = metadata.lastActivity
}

// In-flight dedup for `loadGroupDetails`/`loadGroupUsers`/`loadGroupRoles` — module-level (not
// store state) so it's never made reactive, same pattern as `mediaCache.ts`'s `pending` map.
// Guards against `onMounted` and a `route.params.id` watch (or two components) both asking for
// the same not-yet-cached group in the same tick from firing two backend requests instead of one.
const pendingGroupDetails = new Map<string, Promise<void>>()
const pendingGroupUsers = new Map<string, Promise<void>>()
const pendingGroupRoles = new Map<string, Promise<void>>()
// Groups with a `loadMoreGroupUsers` page request on the wire.
const pendingGroupUsersPage = new Set<string>()

const LAST_CHANNEL_BY_GROUP_KEY = 'lastChannelByGroup'

function readLastChannelByGroup(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(LAST_CHANNEL_BY_GROUP_KEY) || '{}')
  } catch {
    return {}
  }
}

export const useGroupsStore = defineStore('groups', {
  state: () => ({
    groups: [] as Group[],
    selectedGroupId: null as string | null,
    selectedChannelId: null as string | null,
    groupDetails: {} as Record<string, GroupDetails>,
    groupUsers: {} as Record<string, GroupUser[]>,
    // Pagination cursor for `groupUsers`, keyed by groupId: a non-empty token means more pages
    // exist (see `loadMoreGroupUsers`), '' means the list is fully loaded, no entry means the
    // first page hasn't been loaded yet.
    groupUsersNextPageToken: {} as Record<string, string>,
    groupRoles: {} as Record<string, Role[]>,
    // Users currently banned from a group, keyed by groupId. Only populated for groups where the
    // current user has BanMembers permission and has opened the "Banned users" panel (see
    // fetchGroupBans) — not fetched eagerly alongside groupUsers.
    bannedUsers: {} as Record<string, BannedGroupUser[]>,
    // Same cursor semantics as `groupUsersNextPageToken`, for `bannedUsers`.
    bannedUsersNextPageToken: {} as Record<string, string>,
    unreadChannels: {} as Record<string, boolean>,
    unreadGroups: {} as Record<string, string[]>,
    lastReadMessageIds: (typeof window !== 'undefined'
      ? JSON.parse(localStorage.getItem('lastReadMessageIds') || '{}')
      : {}) as Record<string, string>,
    // Last channel opened in each group (text or voice), keyed by groupId — restored when the
    // user navigates back to a group without an explicit `?channel=` in the URL.
    lastChannelByGroup: readLastChannelByGroup(),
    isLoading: false,
    // Separate from `isLoading` (shared with loadGroupDetails) so switching the selected group
    // doesn't flash the sidebar's group-list skeleton on every navigation.
    isLoadingGroups: false,
    error: null as string | null,
    groupsLoaded: false,
  }),
  getters: {
    // Looks up a user's avatar URL by id across every group's cached member list — used wherever
    // only a bare user id is in hand (e.g. a LiveKit participant identity), not a full `GroupUser`
    // object. Scans every group's `groupUsers` rather than just the selected one, same as `setUser`,
    // since the caller isn't always scoped to a single group.
    getUserAvatarUrl: (state) => (userId: string): string => {
      const uid = userId.toLowerCase()
      for (const groupId in state.groupUsers) {
        const match = state.groupUsers[groupId]?.find((u) => u.id.toLowerCase() === uid)
        if (match?.avatar_url) return match.avatar_url
      }
      return ''
    },
    // A user's `common.Subscription` from any group's cached member list - for places that only
    // hold a bare user id (friends: the friends API carries no subscription). `null` if the user
    // isn't in any loaded roster; never fetched just for this.
    getKnownUserSubscription: (state) => (userId: string): Subscription | null => {
      const uid = userId.toLowerCase()
      for (const groupId in state.groupUsers) {
        const match = state.groupUsers[groupId]?.find((u) => u.id.toLowerCase() === uid)
        if (match) return match.subscription
      }
      return null
    },
    // LiveKit State Proxies
    activeRoom: () => liveKitManager.room,
    activeVoiceChannelId: () => liveKitManager.state.activeVoiceChannelId,
    roomState: () => liveKitManager.state.roomState,
    isReconnecting: () => liveKitManager.state.isReconnecting,
    participants: () =>
      liveKitManager.state.participants.map((p) => ({
        sid: p.sid,
        identity: p.identity,
        name: p.name,
        is_local: p.isLocal,
        joined_at: p.joinedAt,
        isSpeaking: resolveIsSpeaking(p),
        isVideoEnabled: p.isVideoEnabled,
        isAudioEnabled: p.isAudioEnabled,
        isScreenShareEnabled: p.isScreenShareEnabled,
        isHandRaised: p.isHandRaised,
        isDeafened: resolveIsDeafened(p),
        connectionQuality: p.connectionQuality,
      })),
    isDeafened: () => liveKitManager.state.isDeafened,
    isMicMuted: () => liveKitManager.state.isMicMuted,
    isCameraEnabled: () => liveKitManager.state.isCameraEnabled,
    isScreenShareEnabled: () => liveKitManager.state.isScreenShareEnabled,
    isNoiseSuppressionEnabled: () => liveKitManager.state.isNoiseSuppressionEnabled,
    isNoiseSuppressionPending: () => liveKitManager.state.isNoiseSuppressionPending,
    isEchoCancellationEnabled: () => liveKitManager.state.isEchoCancellationEnabled,
    isSelfMonitoring: () => liveKitManager.state.isSelfMonitoring,
    micVolume: () => liveKitManager.state.micVolume,
    speakerVolume: () => liveKitManager.state.speakerVolume,
    micSensitivity: () => liveKitManager.state.micSensitivity,
    videoQuality: () => liveKitManager.state.videoQuality,
    screenShareQuality: () => liveKitManager.state.screenShareQuality,
    screenShareFps: () => liveKitManager.state.screenShareFps,
    isScreenShareAudioEnabled: () => liveKitManager.state.isScreenShareAudioEnabled,
    micPermission: () => liveKitManager.state.micPermission,
    cameraPermission: () => liveKitManager.state.cameraPermission,
    // Reactive across every place a mute badge/checkbox can appear (sidebar member list, call
    // tiles) — reads `liveKitManager.state.mutedParticipants`, not a one-off settings lookup, so
    // toggling it anywhere updates everywhere. See `setParticipantMuted`.
    isParticipantMuted: () => (identity: string) => !!liveKitManager.state.mutedParticipants[identity],
    // Whether someone's screen share is being watched ("Смотреть стрим") — see `setScreenShareWatched`.
    isScreenShareWatched: () => (identity: string) => !!liveKitManager.state.watchedScreenShares[identity],
    isChannelUnread: (state) => (channelId: string) => !!state.unreadChannels[channelId.toLowerCase()],
    isGroupUnread: (state) => (groupId: string) => {
      const gid = groupId.toLowerCase()
      return !!state.unreadGroups[gid] && state.unreadGroups[gid].length > 0
    },
    // Still used for the handful of things that are genuinely owner-only regardless of permissions
    // (deleting the group itself, the leave-group/can't-leave-as-owner split) — everything else
    // that used to gate on this should use `hasGroupPermission`/`groupPermissionMask` instead.
    isGroupOwner: (state) => (groupId: string) => !!state.groupDetails[groupId]?.i_am_is_owner,
    // True while `groupUsers[groupId]` is only a prefix of the member list — anything derived from
    // its length (e.g. a member count) is a lower bound until this turns false.
    hasMoreGroupUsers: (state) => (groupId: string) => !!state.groupUsersNextPageToken[groupId],
    hasMoreGroupBans: (state) => (groupId: string) => !!state.bannedUsersNextPageToken[groupId],
    // Every permission the current user effectively has in this group, combined from all of their
    // roles (or every permission if they're the owner/Administrator) — see `computeGroupPermissionMask`.
    groupPermissionMask: (state) => (groupId: string): bigint => computeGroupPermissionMask(state, groupId),
    hasGroupPermission: (state) => (groupId: string, flag: number): boolean =>
      maskHasFlag(computeGroupPermissionMask(state, groupId), flag),
  },
  actions: {
    /**
     * Logout: forget everything loaded for this account - groups, members, roles, unread state,
     * last opened channels - so the next account starts empty. Media cache is left alone.
     * The persisted maps are removed first: `$reset()` re-runs `state()`, which reads them back.
     */
    resetForLogout() {
      pendingGroupDetails.clear()
      pendingGroupUsers.clear()
      pendingGroupRoles.clear()
      pendingGroupUsersPage.clear()
      try {
        localStorage.removeItem(LAST_CHANNEL_BY_GROUP_KEY)
        localStorage.removeItem('lastReadMessageIds')
      } catch {
        // storage unavailable - nothing persisted to remove
      }
      this.$reset()
    },
    findChannel(groupId: string, channelId: string) {
      const group = Object.entries(this.groupDetails).find(([id]) => id.toLowerCase() === groupId.toLowerCase())?.[1]

      return group?.categories
        .flatMap((category) => category.channels)
        .find((channel) => channel.id.toLowerCase() === channelId.toLowerCase())
    },
    // Resolves which group owns a channel by id, regardless of which group is currently selected
    // in the sidebar — needed for permission checks against a voice channel you're connected to
    // (or connecting to) while browsing a different group.
    groupIdForChannel(channelId: string): string | null {
      return Object.keys(this.groupDetails).find((groupId) => this.findChannel(groupId, channelId)) ?? null
    },
    addVoiceChannelUser(groupId: string, channelId: string, user: GroupUser) {
      const channel = this.findChannel(groupId, channelId)
      if (!channel) return

      // `UserConnected`'s SignalR payload is a lighter DTO than the full roster returned by
      // `get_group`/`GetGroupUsers` and can omit fields like `avatarUrl` entirely (they parse to
      // '' via `mapRawGroupUser`'s dual-case lookup). Backfilling from the already-cached group
      // member list here means a fresh manual voice connect doesn't blank out an avatar that a
      // full page reload would otherwise render correctly from the richer payload.
      const cached = this.groupUsers[groupId]?.find((item) => item.id.toLowerCase() === user.id.toLowerCase())
      const merged: GroupUser = cached ? {
        ...user,
        avatar_url: user.avatar_url || cached.avatar_url,
        banner_url: user.banner_url || cached.banner_url,
        display_name: user.display_name || cached.display_name,
        username: user.username || cached.username,
      } : user

      channel.users ??= []
      const userIndex = channel.users.findIndex((item) => item.id.toLowerCase() === merged.id.toLowerCase())

      if (userIndex === -1) {
        channel.users.push(merged)
      } else {
        channel.users[userIndex] = merged
      }
    },
    removeVoiceChannelUser(groupId: string, channelId: string, userId: string) {
      const channel = this.findChannel(groupId, channelId)
      if (!channel?.users) return

      channel.users = channel.users.filter((user) => user.id.toLowerCase() !== userId.toLowerCase())
    },
    setUser<Type extends UserMetadata>(userId: string, metadata: Type) {
      const uid = userId.toLowerCase()

      if (this.groupUsers) {
        for (const groupId in this.groupUsers) {
          const users = this.groupUsers[groupId]
          if (!users) continue

          for (let i = 0; i < users.length; i++) {
            if (users[i]?.id.toLowerCase() === uid) {
              updateUserData(users[i]!, metadata)
            }
          }
        }
      }

      if (this.groupDetails) {
        for (const groupId in this.groupDetails) {
          const categories = this.groupDetails[groupId]?.categories
          if (!categories) continue

          for (let i = 0; i < categories.length; i++) {
            const channels = categories[i]?.channels
            if (!channels) continue

            for (let j = 0; j < channels.length; j++) {
              const users = channels[j]?.users
              if (!users) continue

              for (let k = 0; k < users.length; k++) {
                if (users[k]?.id.toLowerCase() === uid) {
                  updateUserData(users[k]!, metadata)
                }
              }
            }
          }
        }
      }
    },
    setUserPresenceStatus(userId: string, presenceStatus: number, status: string) {
      const uid = userId.toLowerCase()

      for (const groupId in this.groupUsers) {
        const user = this.groupUsers[groupId]?.find((u) => u.id.toLowerCase() === uid)
        if (user) {
          user.user_status = presenceStatus
          user.status = status
        }
      }

      for (const groupId in this.groupDetails) {
        for (const category of this.groupDetails[groupId]?.categories ?? []) {
          for (const channel of category.channels) {
            const user = channel.users?.find((u) => u.id.toLowerCase() === uid)
            if (user) {
              user.user_status = presenceStatus
              user.status = status
            }
          }
        }
      }
    },
    selectGroup(id: string) {
      this.selectedGroupId = id
    },
    selectChannel(id: string | null) {
      if (id === this.selectedChannelId) {
        console.debug(`[GroupsStore] selectChannel(${id}) no-op, already selected`)
        return
      }
      console.debug(`[GroupsStore] selectChannel ${this.selectedChannelId} -> ${id}`)
      this.selectedChannelId = id
      if (id) {
        this.clearChannelUnread(id)
        const groupId = this.groupIdForChannel(id) ?? this.selectedGroupId
        if (groupId && this.lastChannelByGroup[groupId] !== id) {
          this.lastChannelByGroup[groupId] = id
          try {
            localStorage.setItem(LAST_CHANNEL_BY_GROUP_KEY, JSON.stringify(this.lastChannelByGroup))
          } catch {
            // storage unavailable — the in-memory map still covers this session
          }
        }
      }
    },
    markChannelUnread(groupId: string, channelId: string) {
      const gid = groupId.toLowerCase()
      const cid = channelId.toLowerCase()

      if (this.selectedChannelId?.toLowerCase() === cid) return

      // Update unreadChannels
      if (!this.unreadChannels[cid]) {
        this.unreadChannels[cid] = true

        // Update unreadGroups
        if (!this.unreadGroups[gid]) {
          this.unreadGroups[gid] = []
        }
        if (!this.unreadGroups[gid].includes(cid)) {
          this.unreadGroups[gid].push(cid)
        }
      }
    },
    clearChannelUnread(channelId: string) {
      const cid = channelId.toLowerCase()
      if (this.unreadChannels[cid]) {
        delete this.unreadChannels[cid]

        for (const groupId in this.unreadGroups) {
          this.unreadGroups[groupId] = this.unreadGroups[groupId].filter((id) => id.toLowerCase() !== cid)
          if (this.unreadGroups[groupId].length === 0) {
            delete this.unreadGroups[groupId]
          }
        }
      }
    },
    setLastReadMessage(channelId: string, messageId: string) {
      const cid = channelId.toLowerCase()
      const mid = messageId.toLowerCase()
      if (this.lastReadMessageIds[cid] === mid) return
      this.lastReadMessageIds[cid] = mid
      if (typeof window !== 'undefined') {
        localStorage.setItem('lastReadMessageIds', JSON.stringify(this.lastReadMessageIds))
      }
    },
    // `reason` is purely diagnostic (logged, never branched on) — every call site names why it's
    // calling in (mount, route watch, realtime event, ...) so a burst of these in the console can
    // be traced back to its trigger instead of just showing "loadGroupDetails" three times in a row
    // with no way to tell whether that's 3 different causes or 1 cause firing 3 times.
    //
    // Cache-first, same as `fetchGroups`: once a group's details/users/roles are loaded they stay
    // put until something explicitly invalidates them (a mutating action writing straight into
    // `groupDetails`/`groupUsers`/`groupRoles`, or a realtime SignalR event) — group/channel
    // navigation and remounts must never re-hit the backend for data that's already in the store.
    // `force` is the escape hatch for the one case that legitimately needs a fresh fetch instead of
    // a local patch: `UserJoined`, whose payload doesn't carry enough to append the new member
    // locally (see `useGroupsSignalR.ts`).
    async loadGroupDetails(id: string, reason = 'unknown', force = false) {
      if (!force && this.groupDetails[id]) {
        console.debug(`[GroupsStore] loadGroupDetails(${id}) reason=${reason} — cache hit, skipping fetch`)
        return
      }
      console.debug(`[GroupsStore] loadGroupDetails(${id}) reason=${reason} force=${force} — fetching`)
      const inFlight = pendingGroupDetails.get(id)
      if (inFlight) return inFlight

      const request = (async () => {
        const hasData = !!this.groupDetails[id]
        if (!hasData) this.isLoading = true
        try {
          this.groupDetails[id] = await this.getGroup(id)
          // Opening a group loads its notification settings too (cache-first, once per group).
          void useNotificationSettingsStore().ensureLoaded(id)
        } catch {
          // Already recorded (message + Sentry) inside getGroup — this is a fire-and-forget
          // loader, so there's nothing further to do with the error here.
        } finally {
          this.isLoading = false
          pendingGroupDetails.delete(id)
        }
      })()
      pendingGroupDetails.set(id, request)
      return request
    },
    async loadGroupUsers(groupId: string, reason = 'unknown', force = false) {
      if (!force && this.groupUsers[groupId]) {
        console.debug(`[GroupsStore] loadGroupUsers(${groupId}) reason=${reason} — cache hit, skipping fetch`)
        return
      }
      console.debug(`[GroupsStore] loadGroupUsers(${groupId}) reason=${reason} force=${force} — fetching`)
      const inFlight = pendingGroupUsers.get(groupId)
      if (inFlight) return inFlight

      const request = (async () => {
        try {
          const wasFullyLoaded = !!this.groupUsers[groupId] && this.groupUsersNextPageToken[groupId] === ''
          const page = await this.getGroupUsersPage(groupId)
          // Merged, not replaced: on a forced reload (`UserJoined`) the members already loaded
          // from later pages stay on screen. The cursor restarts from this first page — the
          // previous token belonged to the list as it was before the change.
          this.groupUsers[groupId] = mergeGroupUsers(this.groupUsers[groupId] ?? [], page.users)
          this.groupUsersNextPageToken[groupId] = page.next_page_token
          // The whole list was on screen before the reload, so keep it whole: the member that
          // triggered the reload can sit on any page, not necessarily the first.
          while (wasFullyLoaded && this.groupUsersNextPageToken[groupId]) {
            if (!(await this.loadMoreGroupUsers(groupId))) break
          }
        } catch {
          // already recorded inside getGroupUsersPage
        } finally {
          pendingGroupUsers.delete(groupId)
        }
      })()
      pendingGroupUsers.set(groupId, request)
      return request
    },
    // Loads the next page of a group's members (infinite scroll in `ChannelMembersList.vue`).
    // Resolves to whether a page was applied — false when there's nothing more to load, a load
    // is already running, or the request failed — so a caller looping on it knows when to stop.
    async loadMoreGroupUsers(groupId: string): Promise<boolean> {
      const pageToken = this.groupUsersNextPageToken[groupId]
      if (!pageToken || pendingGroupUsersPage.has(groupId)) return false

      pendingGroupUsersPage.add(groupId)
      try {
        const page = await this.getGroupUsersPage(groupId, pageToken)
        // The list was restarted from page one while this was in flight — this page continues a
        // listing that's no longer current.
        if (this.groupUsersNextPageToken[groupId] !== pageToken) return false
        this.groupUsers[groupId] = mergeGroupUsers(this.groupUsers[groupId] ?? [], page.users)
        this.groupUsersNextPageToken[groupId] = page.next_page_token
        return true
      } catch (e) {
        if (isInvalidPageTokenError(e) && this.groupUsersNextPageToken[groupId] === pageToken) {
          try {
            const firstPage = await this.getGroupUsersPage(groupId)
            this.groupUsers[groupId] = firstPage.users
            this.groupUsersNextPageToken[groupId] = firstPage.next_page_token
            return true
          } catch {
            // already recorded inside getGroupUsersPage
          }
        }
        return false
      } finally {
        pendingGroupUsersPage.delete(groupId)
      }
    },
    // Fetched for every member (not just owners) on entering a group — the roles list is needed
    // to render role badges/assignment UI (e.g. the user profile dialog) for any viewer.
    async loadGroupRoles(groupId: string, reason = 'unknown', force = false) {
      if (!force && this.groupRoles[groupId]) {
        console.debug(`[GroupsStore] loadGroupRoles(${groupId}) reason=${reason} — cache hit, skipping fetch`)
        return
      }
      console.debug(`[GroupsStore] loadGroupRoles(${groupId}) reason=${reason} force=${force} — fetching`)
      const inFlight = pendingGroupRoles.get(groupId)
      if (inFlight) return inFlight

      const request = (async () => {
        try {
          this.groupRoles[groupId] = await this.getGroupRoles(groupId)
        } catch {
          // already recorded inside getGroupRoles
        } finally {
          pendingGroupRoles.delete(groupId)
        }
      })()
      pendingGroupRoles.set(groupId, request)
      return request
    },
    async fetchGroups(force = false) {
      // The sidebar re-invokes this on every mount (e.g. re-rendering after a route change), but the
      // group list only needs to be fetched once — subsequent create/delete/leave/join actions already
      // mutate `this.groups` locally, so refetching on every mount was firing a redundant
      // get_user_groups request each time the active group was switched.
      if (this.groupsLoaded && !force) return

      this.isLoadingGroups = true
      this.error = null
      try {
        await runAction(
          (msg) => (this.error = msg),
          { action: 'fetchGroups' },
          async () => {
            this.groups = await invoke<Group[]>('get_user_groups')
            this.groupsLoaded = true
          },
        )
      } finally {
        this.isLoadingGroups = false
      }
    },
    async createGroup(name: string) {
      try {
        return await runAction(
          (msg) => (this.error = msg),
          { action: 'createGroup', extra: { name } },
          async () => {
            const group = await invoke<Group>('create_group', { payload: { name } })
            this.groups.push(group)
            return group
          },
        )
      } catch (e) {
        rereadAfterUncertainWrite(e, () => this.fetchGroups(true))
        throw e
      }
    },
    async createCategory(groupId: string, name: string) {
      if (!this.hasGroupPermission(groupId, PermissionFlag.ManageChannels)) return
      try {
        return await runAction(
          (msg) => (this.error = msg),
          { action: 'createCategory', extra: { groupId, name } },
          async () => {
            const category = await invoke<Category>('create_category', { payload: { group_id: groupId, name } })
            if (this.groupDetails[groupId]) {
              this.groupDetails[groupId].categories.push(category)
            }
            return category
          },
        )
      } catch (e) {
        rereadAfterUncertainWrite(e, () => this.loadGroupDetails(groupId, 'createCategory:uncertain', true))
        throw e
      }
    },
    async updateCategory(groupId: string, categoryId: string, name: string) {
      if (!this.hasGroupPermission(groupId, PermissionFlag.ManageChannels)) return
      return runAction(
        (msg) => (this.error = msg),
        { action: 'updateCategory', extra: { groupId, categoryId } },
        async () => {
          const category = await invoke<Category>('update_category', { payload: { id: categoryId, name } })
          const group = this.groupDetails[groupId]
          if (group) {
            const existing = group.categories.find((c) => c.id === categoryId)
            if (existing) {
              existing.name = category.name
            }
          }
          return category
        },
      )
    },
    async createChannel(groupId: string, categoryId: string, name: string, type: ChannelType = ChannelType.Text) {
      if (!this.hasGroupPermission(groupId, PermissionFlag.ManageChannels)) return
      try {
        return await runAction(
          (msg) => (this.error = msg),
          { action: 'createChannel', extra: { groupId, categoryId, name } },
          async () => {
            const channel = await invoke<Channel>('create_channel', { payload: { category_id: categoryId, name, type } })
            const group = this.groupDetails[groupId]
            if (group) {
              const category = group.categories.find((c) => c.id === categoryId)
              if (category) {
                category.channels.push(channel)
              }
            }
            return channel
          },
        )
      } catch (e) {
        rereadAfterUncertainWrite(e, () => this.loadGroupDetails(groupId, 'createChannel:uncertain', true))
        throw e
      }
    },
    async updateChannel(channelId: string, name: string, description: string, userLimit?: number, bitrate?: number) {
      const containingGroup = Object.entries(this.groupDetails).find(([, group]) =>
        group.categories.some((category) => category.channels.some((c) => c.id === channelId)),
      )
      if (!containingGroup || !this.hasGroupPermission(containingGroup[0], PermissionFlag.ManageChannels)) return
      return runAction(
        (msg) => (this.error = msg),
        { action: 'updateChannel', extra: { channelId } },
        async () => {
          const success = await invoke<boolean>('update_channel', {
            payload: {
              channel_id: channelId,
              name,
              description,
              user_limit: userLimit,
              bitrate,
            },
          })

          if (!success) {
            throw new Error('Сервер не обновил канал')
          }

          for (const group of Object.values(this.groupDetails)) {
            const channel = group.categories
              .flatMap((category) => category.channels)
              .find((item) => item.id === channelId)
            if (channel) {
              channel.name = name
              channel.description = description
              if (userLimit !== undefined) channel.user_limit = userLimit
              if (bitrate !== undefined) channel.bitrate = bitrate
              break
            }
          }
        },
      )
    },
    // Требует ManageRoles (не ManageChannels) на бэкенде - этот флаг решает, чьи permission
    // overwrite'ы (канала или категории) вообще действуют, так что логически это часть системы
    // прав, а не общих настроек канала.
    async setChannelCategorySync(channelId: string, isSynced: boolean) {
      const containingGroup = Object.entries(this.groupDetails).find(([, group]) =>
        group.categories.some((category) => category.channels.some((c) => c.id === channelId)),
      )
      if (!containingGroup || !this.hasGroupPermission(containingGroup[0], PermissionFlag.ManageRoles)) return
      return runAction(
        (msg) => (this.error = msg),
        { action: 'setChannelCategorySync', extra: { channelId, isSynced } },
        async () => {
          const updated = await invoke<Channel>('set_channel_category_sync', {
            payload: { channel_id: channelId, is_synced: isSynced },
          })

          for (const group of Object.values(this.groupDetails)) {
            const channel = group.categories
              .flatMap((category) => category.channels)
              .find((item) => item.id === channelId)
            if (channel) {
              channel.is_category_permissions_sync = updated.is_category_permissions_sync
              break
            }
          }

          return updated
        },
      )
    },
    async moveChannel(
      groupId: string,
      targetCategoryId: string,
      channelId: string,
      previousChannelId?: string,
      nextChannelId?: string,
    ) {
      if (!this.hasGroupPermission(groupId, PermissionFlag.ManageChannels)) return
      const group = this.groupDetails[groupId]
      if (!group) return

      let sourceCategory: Category | undefined
      let channel: Channel | undefined
      let sourceIndex = -1
      for (const category of group.categories) {
        const index = category.channels.findIndex((c) => c.id === channelId)
        if (index !== -1) {
          sourceCategory = category
          sourceIndex = index
          channel = category.channels[index]
          break
        }
      }
      const targetCategory = group.categories.find((c) => c.id === targetCategoryId)
      if (!channel || !sourceCategory || !targetCategory) return

      return runAction(
        (msg) => (this.error = msg),
        { action: 'moveChannel', extra: { groupId, channelId } },
        async () => {
          await invoke('move_channel', {
            payload: {
              channel_id: channelId,
              previous_channel_id: previousChannelId,
              next_channel_id: nextChannelId,
              target_category_id: targetCategoryId,
            },
          })

          sourceCategory.channels.splice(sourceIndex, 1)
          let insertIndex = targetCategory.channels.findIndex((c) => c.id === nextChannelId)
          if (insertIndex === -1) {
            insertIndex = previousChannelId
              ? targetCategory.channels.findIndex((c) => c.id === previousChannelId) + 1
              : targetCategory.channels.length
          }
          channel.category_id = targetCategoryId
          targetCategory.channels.splice(insertIndex, 0, channel)
        },
      )
    },
    async moveCategory(groupId: string, categoryId: string, previousCategoryId?: string, nextCategoryId?: string) {
      if (!this.hasGroupPermission(groupId, PermissionFlag.ManageChannels)) return
      const group = this.groupDetails[groupId]
      if (!group) return

      const sourceIndex = group.categories.findIndex((c) => c.id === categoryId)
      if (sourceIndex === -1) return
      const category = group.categories[sourceIndex]

      return runAction(
        (msg) => (this.error = msg),
        { action: 'moveCategory', extra: { groupId, categoryId } },
        async () => {
          await invoke('move_category', {
            payload: {
              category_id: categoryId,
              previous_category_id: previousCategoryId,
              next_category_id: nextCategoryId,
            },
          })

          group.categories.splice(sourceIndex, 1)
          let insertIndex = group.categories.findIndex((c) => c.id === nextCategoryId)
          if (insertIndex === -1) {
            insertIndex = previousCategoryId
              ? group.categories.findIndex((c) => c.id === previousCategoryId) + 1
              : group.categories.length
          }
          group.categories.splice(insertIndex, 0, category)
        },
      )
    },
    async deleteCategory(groupId: string, categoryId: string) {
      if (!this.hasGroupPermission(groupId, PermissionFlag.ManageChannels)) return
      return runAction(
        (msg) => (this.error = msg),
        { action: 'deleteCategory', extra: { groupId, categoryId } },
        async () => {
          await invoke('delete_category', { id: categoryId })
          if (this.groupDetails[groupId]) {
            this.groupDetails[groupId].categories = this.groupDetails[groupId].categories.filter(
              (c) => c.id !== categoryId,
            )
          }
        },
      )
    },
    async deleteChannel(groupId: string, categoryId: string, channelId: string) {
      if (!this.hasGroupPermission(groupId, PermissionFlag.ManageChannels)) return
      return runAction(
        (msg) => (this.error = msg),
        { action: 'deleteChannel', extra: { groupId, categoryId, channelId } },
        async () => {
          await invoke('delete_channel', { id: channelId })
          const group = this.groupDetails[groupId]
          if (group) {
            const category = group.categories.find((c) => c.id === categoryId)
            if (category) {
              category.channels = category.channels.filter((c) => c.id !== channelId)
            }
          }
        },
      )
    },
    async kickUser(groupId: string, userId: string) {
      if (!this.hasGroupPermission(groupId, PermissionFlag.KickMembers)) return
      return runAction(
        (msg) => (this.error = msg),
        { action: 'kickUser', extra: { groupId, userId } },
        async () => {
          await invoke('kick_user', { payload: { group_id: groupId, user_id: userId } })
          if (this.groupUsers[groupId]) {
            this.groupUsers[groupId] = this.groupUsers[groupId].filter((u) => u.id !== userId)
          }
        },
      )
    },
    // Members that arrived some other way than the group's own pages (the channel member list) join
    // the group's member cache, so profiles, permissions and presence updates see them too. Only into
    // a cache that's already there: creating it here would make `loadGroupUsers` take a few members
    // for the whole loaded list and never fetch the group's own pages.
    mergeGroupMembers(groupId: string, users: GroupUser[]) {
      if (!users.length || !this.groupUsers[groupId]) return
      this.groupUsers[groupId] = mergeGroupUsers(this.groupUsers[groupId], users)
    },
    removeGroupUser(groupId: string, userId: string) {
      if (this.groupUsers[groupId]) {
        this.groupUsers[groupId] = this.groupUsers[groupId].filter((u) => u.id !== userId)
      }
    },
    async handleSelfKicked(groupId: string) {
      if (this.activeVoiceChannelId && this.findChannel(groupId, this.activeVoiceChannelId)) {
        try {
          await this.disconnectChannel()
        } catch (e) {
          // already logged to Sentry inside disconnectChannel; don't let it block cleanup below
        }
      }
      delete this.groupDetails[groupId]
      delete this.groupUsers[groupId]
      delete this.groupUsersNextPageToken[groupId]
      this.groups = this.groups.filter((g) => g.id !== groupId)
      if (this.selectedGroupId === groupId) {
        this.selectedGroupId = null
        this.selectedChannelId = null
      }
    },
    async banUser(groupId: string, userId: string, reason?: string) {
      if (!this.hasGroupPermission(groupId, PermissionFlag.BanMembers)) return
      return runAction(
        (msg) => (this.error = msg),
        { action: 'banUser', extra: { groupId, userId } },
        async () => {
          await invoke('ban_user', { payload: { group_id: groupId, user_id: userId, reason: reason ?? '' } })
          // BanUser also kicks the target if they're still a member, mirror kickUser's local cleanup.
          if (this.groupUsers[groupId]) {
            this.groupUsers[groupId] = this.groupUsers[groupId].filter((u) => u.id !== userId)
          }
        },
      )
    },
    async unbanUser(groupId: string, userId: string) {
      const previous = this.bannedUsers[groupId]
      if (previous) {
        this.bannedUsers[groupId] = previous.filter((b) => b.user.id !== userId)
      }
      try {
        await invoke('unban_user', { payload: { group_id: groupId, user_id: userId } })
        this.restartGroupBansIfPartial(groupId)
      } catch (e: any) {
        if (previous) this.bannedUsers[groupId] = previous
        this.error = e?.message || String(e)
        Sentry.captureException(e, { extra: { action: 'unbanUser', groupId, userId } })
        throw e
      }
    },
    async getGroupBansPage(groupId: string, pageToken = '') {
      return runAction(
        (msg) => (this.error = msg),
        { action: 'getGroupBans', extra: { groupId, continued: !!pageToken } },
        () => invoke<GroupBansPage>('get_group_bans', { groupId, pageSize: GROUP_BANS_PAGE_SIZE, pageToken }),
      ) as Promise<GroupBansPage>
    },
    // Loads (or restarts from) the first page of a group's bans — see `loadMoreGroupBans` for
    // the rest.
    async fetchGroupBans(groupId: string) {
      if (!this.hasGroupPermission(groupId, PermissionFlag.BanMembers)) return
      const page = await this.getGroupBansPage(groupId)
      this.bannedUsers[groupId] = page.bans
      this.bannedUsersNextPageToken[groupId] = page.next_page_token
      return page
    },
    async loadMoreGroupBans(groupId: string) {
      const pageToken = this.bannedUsersNextPageToken[groupId]
      if (!pageToken) return
      try {
        const page = await this.getGroupBansPage(groupId, pageToken)
        // Restarted from page one while this was in flight — see `loadMoreGroupUsers`.
        if (this.bannedUsersNextPageToken[groupId] !== pageToken) return
        const known = new Set((this.bannedUsers[groupId] ?? []).map((b) => b.user.id))
        this.bannedUsers[groupId] = [
          ...(this.bannedUsers[groupId] ?? []),
          ...page.bans.filter((b) => !known.has(b.user.id)),
        ]
        this.bannedUsersNextPageToken[groupId] = page.next_page_token
      } catch (e) {
        if (!isInvalidPageTokenError(e)) throw e
        await this.fetchGroupBans(groupId)
      }
    },
    // The ban list just changed. If it's fully loaded the local edit is all that's needed; if
    // only a prefix is loaded, the held page token describes the list as it was before the
    // change and must not be continued from — start over at the first page.
    restartGroupBansIfPartial(groupId: string) {
      if (!this.bannedUsersNextPageToken[groupId]) return
      this.fetchGroupBans(groupId).catch(() => {
        // already recorded inside getGroupBansPage
      })
    },
    removeGroupBan(groupId: string, userId: string) {
      if (this.bannedUsers[groupId]) {
        this.bannedUsers[groupId] = this.bannedUsers[groupId].filter((b) => b.user.id !== userId)
        this.restartGroupBansIfPartial(groupId)
      }
    },
    async deleteGroup(id: string) {
      return runAction(
        (msg) => (this.error = msg),
        { action: 'deleteGroup', extra: { id } },
        async () => {
          await invoke('delete_group', { id })
          this.groups = this.groups.filter((g) => g.id !== id)
        },
      )
    },
    // The group row of the notification settings (FOREVER / UNMUTE): that store does the optimistic
    // update + rollback and keeps `is_muted` in sync with the row.
    async setGroupMute(groupId: string, muted: boolean) {
      return useNotificationSettingsStore().mute(
        groupId,
        NotificationTargetType.Group,
        groupId,
        muted ? MuteDuration.Forever : MuteDuration.Unmute,
      )
    },
    async leaveGroup(groupId: string) {
      return runAction(
        (msg) => (this.error = msg),
        { action: 'leaveGroup', extra: { groupId } },
        async () => {
          await invoke('leave_group', { payload: { group_id: groupId } })
          this.groups = this.groups.filter((g) => g.id !== groupId)
        },
      )
    },
    async updateGroup(payload: {
      id: string
      name?: string
      description?: string
      access_type?: number
      verification_level?: number
      /** NotificationLevel: ALL_MESSAGES / ONLY_MENTIONS (ManageGroup). */
      default_notification_level?: number
    }) {
      return runAction(
        (msg) => (this.error = msg),
        { action: 'updateGroup', extra: { groupId: payload.id } },
        async () => {
          const group = await invoke<Group>('update_group', { payload })
          this.mergeGroupPatch(group)
          // `Group` doesn't carry the default level — patch it in from what was sent.
          if (payload.default_notification_level !== undefined) {
            if (this.groupDetails[payload.id]) this.groupDetails[payload.id]!.default_notification_level = payload.default_notification_level
            useNotificationSettingsStore().setGroupDefault(payload.id, payload.default_notification_level)
          }
          return group
        },
      )
    },
    mergeGroupPatch(group: Group) {
      const index = this.groups.findIndex((g) => g.id === group.id)
      if (index !== -1) {
        this.groups[index] = { ...this.groups[index], ...group }
      }
      if (this.groupDetails[group.id]) {
        this.groupDetails[group.id] = { ...this.groupDetails[group.id], ...group }
      }
    },
    // Applies a full group snapshot pushed over SignalR (Category/Channel created/updated/deleted/moved,
    // GroupUpdated, ...). Category/Channel events carry the whole categories/channels tree in server order,
    // so those get rebuilt wholesale. GroupUpdated's payload always sends an empty Categories array (it's
    // not a structural event), so that branch must never touch the existing category tree.
    applyGroupSnapshot(payload: Record<string, unknown>, eventType: string) {
      const stringValue = dualCaseString

      const groupId = stringValue(payload, 'id', 'Id')
      if (!groupId) return

      const existingGroup = this.groupDetails[groupId]
      const isStructuralEvent = eventType !== 'GroupUpdated'

      let categories = existingGroup?.categories ?? []
      if (isStructuralEvent) {
        const existingChannels = existingGroup
          ? new Map(existingGroup.categories.flatMap((c) => c.channels).map((c) => [c.id, c]))
          : new Map<string, Channel>()

        const rawCategories = (payload.categories ?? payload.Categories ?? []) as Record<string, unknown>[]
        categories = rawCategories
          .map((rawCategory) => {
            const categoryId = stringValue(rawCategory, 'id', 'Id')
            const rawChannels = (rawCategory.channels ?? rawCategory.Channels ?? []) as Record<string, unknown>[]

            const channels: Channel[] = rawChannels
              .map((rawChannel) => {
                const channelId = stringValue(rawChannel, 'id', 'Id')
                const rawUsers = (rawChannel.users ?? rawChannel.Users ?? []) as Record<string, unknown>[]
                const users = rawUsers.map((u) => mapRawChannelUser(u))
                const existingChannel = existingChannels.get(channelId)

                return {
                  id: channelId,
                  name: stringValue(rawChannel, 'name', 'Name'),
                  type: Number(rawChannel.type ?? rawChannel.Type ?? 0),
                  position: stringValue(rawChannel, 'position', 'Position'),
                  description: stringValue(rawChannel, 'description', 'Description') || undefined,
                  users: users.length > 0 ? users : (existingChannel?.users ?? []),
                  category_id: categoryId,
                }
              })
              .sort((a, b) => a.position.localeCompare(b.position))

            return {
              id: categoryId,
              name: stringValue(rawCategory, 'name', 'Name'),
              position: stringValue(rawCategory, 'position', 'Position'),
              channels,
            }
          })
          .sort((a, b) => a.position.localeCompare(b.position))
      }

      const name = stringValue(payload, 'name', 'Name')
      const accessType = Number(payload.accessType ?? payload.AccessType ?? existingGroup?.access_type ?? 0)
      const verificationLevel = Number(
        payload.verificationLevel ?? payload.VerificationLevel ?? existingGroup?.verification_level ?? 0,
      )
      const avatarId = stringValue(payload, 'avatarId', 'AvatarId')
      const bannerId = stringValue(payload, 'bannerId', 'BannerId')
      // Media ids resolve to signed URLs server-side (see uploadGroupAvatar/-Banner) — the socket payload
      // only carries the id, so an id change needs a follow-up fetch to pick up a fresh avatar/banner URL.
      const avatarChanged = !!avatarId && avatarId !== existingGroup?.avatar_id
      const bannerChanged = !!bannerId && bannerId !== existingGroup?.banner_id

      // Only rebuild groupDetails for groups we've already loaded — avoids inserting an entry with a
      // guessed i_am_is_owner before the real fetch (via loadGroupDetails) has happened.
      const rawDefaultLevel = payload.defaultNotificationLevel ?? payload.DefaultNotificationLevel ?? payload.default_notification_level
      const defaultNotificationLevel = rawDefaultLevel == null ? existingGroup?.default_notification_level : Number(rawDefaultLevel)
      if (rawDefaultLevel != null && defaultNotificationLevel !== undefined && Number.isFinite(defaultNotificationLevel)) {
        useNotificationSettingsStore().setGroupDefault(groupId, defaultNotificationLevel)
      }

      if (existingGroup) {
        this.groupDetails[groupId] = {
          ...existingGroup,
          default_notification_level: defaultNotificationLevel,
          name: name || existingGroup.name,
          access_type: accessType,
          verification_level: verificationLevel,
          avatar_id: avatarId || existingGroup.avatar_id,
          banner_id: bannerId || existingGroup.banner_id,
          categories,
        }
      }

      const listIndex = this.groups.findIndex((g) => g.id === groupId)
      if (listIndex !== -1) {
        this.groups[listIndex] = {
          ...this.groups[listIndex],
          name: name || this.groups[listIndex].name,
          access_type: accessType,
          verification_level: verificationLevel,
          avatar_id: avatarId || this.groups[listIndex].avatar_id,
          banner_id: bannerId || this.groups[listIndex].banner_id,
        }
      }

      if (avatarChanged || bannerChanged) {
        this.refreshGroupMedia(groupId)
      }
    },
    // Best-effort follow-up fetch to resolve avatar_url/banner_url after applyGroupSnapshot sees the
    // underlying media id change — failures are non-fatal, the stale image just stays until next sync.
    async refreshGroupMedia(groupId: string) {
      try {
        const details = await this.getGroup(groupId)
        if (this.groupDetails[groupId]) {
          this.groupDetails[groupId].avatar_url = details.avatar_url
          this.groupDetails[groupId].banner_url = details.banner_url
        }
        const listIndex = this.groups.findIndex((g) => g.id === groupId)
        if (listIndex !== -1) {
          this.groups[listIndex].avatar_url = details.avatar_url
          this.groups[listIndex].banner_url = details.banner_url
        }
      } catch (e) {
        console.error('Failed to refresh group media URLs:', e)
      }
    },
    async uploadGroupAvatar(groupId: string, file: File) {
      return runAction(
        (msg) => (this.error = msg),
        { action: 'uploadGroupAvatar', extra: { groupId } },
        async () => {
          const contentType = file.type || 'application/octet-stream'
          const initiate = await invoke<{ media_id: string; upload_url: string; expires_at?: string }>(
            'initiate_group_avatar_upload',
            {
              groupId,
              contentType,
              sizeBytes: file.size,
            },
          )

          const uploadResponse = await fetch(initiate.upload_url, {
            method: 'PUT',
            headers: { 'Content-Type': contentType },
            body: file,
          })
          if (!uploadResponse.ok) throw new Error(`S3 upload failed with status ${uploadResponse.status}`)

          const group = await invoke<Group>('confirm_group_avatar_upload', {
            groupId,
            avatarId: initiate.media_id,
          })
          this.mergeGroupPatch(group)
          return group
        },
      )
    },
    async uploadGroupBanner(groupId: string, file: File) {
      return runAction(
        (msg) => (this.error = msg),
        { action: 'uploadGroupBanner', extra: { groupId } },
        async () => {
          const contentType = file.type || 'application/octet-stream'
          const initiate = await invoke<{ media_id: string; upload_url: string; expires_at?: string }>(
            'initiate_group_banner_upload',
            {
              groupId,
              contentType,
              sizeBytes: file.size,
            },
          )

          const uploadResponse = await fetch(initiate.upload_url, {
            method: 'PUT',
            headers: { 'Content-Type': contentType },
            body: file,
          })
          if (!uploadResponse.ok) throw new Error(`S3 upload failed with status ${uploadResponse.status}`)

          const group = await invoke<Group>('confirm_group_banner_upload', {
            groupId,
            bannerId: initiate.media_id,
          })
          this.mergeGroupPatch(group)
          return group
        },
      )
    },
    async deleteGroupAvatar(groupId: string) {
      return runAction(
        (msg) => (this.error = msg),
        { action: 'deleteGroupAvatar', extra: { groupId } },
        async () => {
          const group = await invoke<Group>('delete_group_avatar', { groupId })
          this.mergeGroupPatch(group)
          return group
        },
      )
    },
    async deleteGroupBanner(groupId: string) {
      return runAction(
        (msg) => (this.error = msg),
        { action: 'deleteGroupBanner', extra: { groupId } },
        async () => {
          const group = await invoke<Group>('delete_group_banner', { groupId })
          this.mergeGroupPatch(group)
          return group
        },
      )
    },
    async getGroup(id: string) {
      return runAction(
        (msg) => (this.error = msg),
        { action: 'getGroup', extra: { id } },
        () => invoke<GroupDetails>('get_group', { id }),
      ) as Promise<GroupDetails>
    },
    async getGroupUsersPage(groupId: string, pageToken = '') {
      return runAction(
        (msg) => (this.error = msg),
        { action: 'getGroupUsers', extra: { groupId, continued: !!pageToken } },
        () => invoke<GroupUsersPage>('get_group_users', { groupId, pageSize: GROUP_USERS_PAGE_SIZE, pageToken }),
      ) as Promise<GroupUsersPage>
    },
    // Requested without a page size on purpose: that way the server returns every role in one
    // response, which permission calculation and role rendering rely on.
    async getGroupRoles(groupId: string) {
      return runAction(
        (msg) => (this.error = msg),
        { action: 'getGroupRoles', extra: { groupId } },
        () => invoke<Role[]>('get_group_roles', { groupId }),
      ) as Promise<Role[]>
    },
    // Every mutating role action below writes straight into `this.groupRoles`, which is the same
    // state `MessageGroup.vue`, `MessageItem.vue`, `ChannelMembersList.vue`, `UserProfileDialog.vue`
    // etc. read `getRoleColor`/role name from — that's what makes a color/name edit propagate
    // everywhere reactively instead of only updating whatever local copy the editor dialog held.
    _findRoleGroupId(roleId: string): string | null {
      for (const [groupId, roles] of Object.entries(this.groupRoles)) {
        if (roles.some(r => r.id === roleId)) return groupId
      }
      return null
    },
    async createRole(groupId: string, name: string, color?: string, permissions: number = 0, hoist?: boolean) {
      let role: Role
      try {
        role = (await runAction(
          (msg) => (this.error = msg),
          { action: 'createRole', extra: { groupId } },
          () => invoke<Role>('create_role', { payload: { group_id: groupId, name, color, permissions, hoist } }),
        )) as Role
      } catch (e) {
        rereadAfterUncertainWrite(e, () => this.loadGroupRoles(groupId, 'createRole:uncertain', true))
        throw e
      }
      if (this.groupRoles[groupId]) {
        // The backend inserts the new role just below all existing custom roles (ascending by
        // `position`, strictly above '@everyone') - splice it into the matching spot in the local
        // ascending array instead of blindly appending, or it shows up as the most senior role
        // (past '@everyone', even) until the next reload.
        const roles = this.groupRoles[groupId]
        const insertIndex = roles.findIndex(r => r.position > role.position)
        this.groupRoles[groupId] = insertIndex === -1
          ? [...roles, role]
          : [...roles.slice(0, insertIndex), role, ...roles.slice(insertIndex)]
      }
      return role
    },
    // `hoist` left out keeps the role's current value.
    async updateRole(roleId: string, name: string, permissions: number, color?: string, hoist?: boolean) {
      const role = await runAction(
        (msg) => (this.error = msg),
        { action: 'updateRole', extra: { roleId } },
        () => invoke<Role>('update_role', { payload: { role_id: roleId, name, permissions, color, hoist } }),
      )
      const groupId = this._findRoleGroupId(roleId)
      if (groupId) this.groupRoles[groupId] = this.groupRoles[groupId].map(r => r.id === role.id ? role : r)
      return role
    },
    async moveRole(roleId: string, previousRoleId?: string, nextRoleId?: string) {
      const groupId = this._findRoleGroupId(roleId)
      // Snapshotted before the call: the browser build's REST endpoint only returns `{}` on a
      // move (unlike Tauri's, which echoes the full, unchanged-except-for-order Role — see
      // `~/lib/platform/browser/routes.ts`), so the response can't be trusted to splice back in.
      // A move never changes name/color/permissions, so re-inserting the pre-call copy is exactly
      // equivalent either way.
      const existingRole = groupId ? this.groupRoles[groupId]?.find(r => r.id === roleId) : undefined

      const role = await runAction(
        (msg) => (this.error = msg),
        { action: 'moveRole', extra: { roleId } },
        () =>
          invoke<Role>('move_role', {
            payload: { role_id: roleId, previous_role_id: previousRoleId, next_role_id: nextRoleId },
          }),
      )
      if (groupId) {
        const roles = this.groupRoles[groupId].filter(r => r.id !== roleId)
        let insertIndex = nextRoleId ? roles.findIndex(r => r.id === nextRoleId) : -1
        if (insertIndex === -1) {
          insertIndex = previousRoleId ? roles.findIndex(r => r.id === previousRoleId) + 1 : roles.length
        }
        roles.splice(insertIndex, 0, existingRole ?? role)
        this.groupRoles[groupId] = roles
      }
      return role
    },
    async deleteRole(roleId: string) {
      const groupId = this._findRoleGroupId(roleId)
      const result = await runAction(
        (msg) => (this.error = msg),
        { action: 'deleteRole', extra: { roleId } },
        () => invoke<{ id: string; success: boolean }>('delete_role', { roleId }),
      )
      if (groupId) this.groupRoles[groupId] = this.groupRoles[groupId].filter(r => r.id !== roleId)
      return result
    },
    async getChannelPermissionOverwrites(channelId: string) {
      return runAction(
        (msg) => (this.error = msg),
        { action: 'getChannelPermissionOverwrites', extra: { channelId } },
        () => invoke<ChannelPermissionOverwrite[]>('get_channel_permission_overwrites', { channelId }),
      ) as Promise<ChannelPermissionOverwrite[]>
    },
    async setChannelPermissionOverwrite(channelId: string, targetId: string, isRoleTarget: boolean, allow: number, deny: number) {
      return runAction(
        (msg) => (this.error = msg),
        { action: 'setChannelPermissionOverwrite', extra: { channelId, targetId } },
        () =>
          invoke<void>('set_channel_permission_overwrite', {
            payload: { channel_id: channelId, target_id: targetId, is_role_target: isRoleTarget, allow, deny },
          }),
      )
    },
    async deleteChannelPermissionOverwrite(channelId: string, targetId: string, isRoleTarget: boolean) {
      return runAction(
        (msg) => (this.error = msg),
        { action: 'deleteChannelPermissionOverwrite', extra: { channelId, targetId } },
        () =>
          invoke<void>('delete_channel_permission_overwrite', {
            payload: { channel_id: channelId, target_id: targetId, is_role_target: isRoleTarget },
          }),
      )
    },
    async getCategoryPermissionOverwrites(categoryId: string) {
      return runAction(
        (msg) => (this.error = msg),
        { action: 'getCategoryPermissionOverwrites', extra: { categoryId } },
        () => invoke<CategoryPermissionOverwrite[]>('get_category_permission_overwrites', { categoryId }),
      ) as Promise<CategoryPermissionOverwrite[]>
    },
    async setCategoryPermissionOverwrite(categoryId: string, targetId: string, isRoleTarget: boolean, allow: number, deny: number) {
      return runAction(
        (msg) => (this.error = msg),
        { action: 'setCategoryPermissionOverwrite', extra: { categoryId, targetId } },
        () =>
          invoke<void>('set_category_permission_overwrite', {
            payload: { category_id: categoryId, target_id: targetId, is_role_target: isRoleTarget, allow, deny },
          }),
      )
    },
    async deleteCategoryPermissionOverwrite(categoryId: string, targetId: string, isRoleTarget: boolean) {
      return runAction(
        (msg) => (this.error = msg),
        { action: 'deleteCategoryPermissionOverwrite', extra: { categoryId, targetId } },
        () =>
          invoke<void>('delete_category_permission_overwrite', {
            payload: { category_id: categoryId, target_id: targetId, is_role_target: isRoleTarget },
          }),
      )
    },
    async assignRole(groupId: string, targetUserId: string, roleId: string) {
      return runAction(
        (msg) => (this.error = msg),
        { action: 'assignRole', extra: { groupId, targetUserId, roleId } },
        async () => {
          await invoke('assign_role', { payload: { group_id: groupId, target_user_id: targetUserId, role_id: roleId } })
          const user = this.groupUsers[groupId]?.find((u) => u.id === targetUserId)
          if (user && !user.role_ids.includes(roleId)) user.role_ids.push(roleId)
        },
      )
    },
    async removeRole(groupId: string, targetUserId: string, roleId: string) {
      return runAction(
        (msg) => (this.error = msg),
        { action: 'removeRole', extra: { groupId, targetUserId, roleId } },
        async () => {
          await invoke('remove_role', { payload: { group_id: groupId, target_user_id: targetUserId, role_id: roleId } })
          const user = this.groupUsers[groupId]?.find((u) => u.id === targetUserId)
          if (user) user.role_ids = user.role_ids.filter((id) => id !== roleId)
        },
      )
    },
    async createInvite(groupId: string, maxUses: number = 0, expiresAt?: string) {
      return runAction(
        (msg) => (this.error = msg),
        { action: 'createInvite', extra: { groupId } },
        () =>
          invoke<Invite>('create_invite', { payload: { group_id: groupId, max_uses: maxUses, expires_at: expiresAt } }),
      )
    },
    async joinByInvite(inviteCode: string) {
      try {
        return await runAction(
          (msg) => (this.error = msg),
          { action: 'joinByInvite' },
          async () => {
            const response = await invoke<{ group_id: string; success: boolean }>('join_by_invite', {
              payload: { invite_code: inviteCode },
            })
            if (response.success) {
              // Presence events about a group's members only start reaching a newly-joined user
              // (and events about them, the other members) up to ~30s after the join, so member
              // statuses must come from a fresh GetGroupUsers rather than from a roster cached
              // during an earlier membership that realtime events have since stopped updating.
              delete this.groupUsers[response.group_id]
              delete this.groupUsersNextPageToken[response.group_id]
              await this.fetchGroups(true)
            }
            return response
          },
        )
      } catch (e) {
        rereadAfterUncertainWrite(e, () => this.fetchGroups(true))
        throw e
      }
    },
    // One page of a group's invites; `pageToken` is the previous page's `next_page_token`
    // (omit for the first page). See the pagination rules next to `isInvalidPageTokenError`.
    async getGroupInvites(groupId: string, pageToken = '') {
      return runAction(
        (msg) => (this.error = msg),
        { action: 'getGroupInvites', extra: { groupId, continued: !!pageToken } },
        () => invoke<GroupInvitesPage>('get_group_invites', { groupId, pageSize: GROUP_INVITES_PAGE_SIZE, pageToken }),
      ) as Promise<GroupInvitesPage>
    },
    async updateInvite(id: string, maxUses: number = 0, expiresAt?: string) {
      return runAction(
        (msg) => (this.error = msg),
        { action: 'updateInvite', extra: { id } },
        () => invoke<Invite>('update_invite', { payload: { id, max_uses: maxUses, expires_at: expiresAt } }),
      )
    },
    async deleteInvite(id: string) {
      return runAction(
        (msg) => (this.error = msg),
        { action: 'deleteInvite', extra: { id } },
        () => invoke('delete_invite', { id }),
      )
    },

    // --- Voice Logic (Delegated to LiveKitManager) ---

    async connectToVoice(channelId: string) {
      const groupId = this.groupIdForChannel(channelId) ?? this.selectedGroupId
      const canSpeak = !groupId || this.hasGroupPermission(groupId, PermissionFlag.Speak)
      const audioBitrateKbps = groupId ? this.findChannel(groupId, channelId)?.bitrate : undefined
      return runAction(
        (msg) => (this.error = msg),
        { action: 'connectToVoice', extra: { channelId } },
        () => voiceConnectionCoordinator.connect(channelId, { canSpeak, audioBitrateKbps }),
      )
    },
    async disconnectChannel() {
      return runAction(
        (msg) => (this.error = msg),
        { action: 'disconnectChannel' },
        () => voiceConnectionCoordinator.disconnect(),
      )
    },
    async refreshMediaPermissions() {
      await liveKitManager.refreshMediaPermissions()
    },
    // `activeVoiceChannelId`'s owning group, not necessarily `selectedGroupId` — you can toggle
    // mic/camera from the call UI while browsing a different group in the sidebar.
    activeVoiceGroupId(): string | null {
      return this.activeVoiceChannelId ? (this.groupIdForChannel(this.activeVoiceChannelId) ?? this.selectedGroupId) : this.selectedGroupId
    },
    async toggleMic(deviceId?: string) {
      const groupId = this.activeVoiceGroupId()
      if (groupId && !this.hasGroupPermission(groupId, PermissionFlag.Speak)) return
      await liveKitManager.toggleMic(deviceId)
    },
    async toggleCamera(deviceId?: string) {
      const groupId = this.activeVoiceGroupId()
      if (groupId && !this.hasGroupPermission(groupId, PermissionFlag.Video)) return
      await liveKitManager.toggleCamera(deviceId)
    },
    async toggleScreenShare() {
      const groupId = this.activeVoiceGroupId()
      if (groupId && !this.hasGroupPermission(groupId, PermissionFlag.Video)) return
      await liveKitManager.toggleScreenShare()
    },
    async startScreenShare(stream: MediaStream, quality: ScreenShareQuality, fps: number, includeAudio = true) {
      await liveKitManager.startScreenShare(stream, quality, fps, includeAudio)
    },
    async toggleDeaf() {
      await liveKitManager.toggleDeaf()
    },
    async setNoiseSuppressionEnabled(enabled: boolean) {
      await liveKitManager.setNoiseSuppressionEnabled(enabled)
    },
    async toggleNoiseSuppression() {
      await liveKitManager.setNoiseSuppressionEnabled(!liveKitManager.state.isNoiseSuppressionEnabled)
    },
    async toggleEchoCancellation() {
      await liveKitManager.setEchoCancellationEnabled(!liveKitManager.state.isEchoCancellationEnabled)
    },
    async setSelfMonitoring(enabled: boolean) {
      await liveKitManager.setSelfMonitoring(enabled)
    },
    setMicVolume(volume: number) {
      liveKitManager.setMicVolume(volume)
    },
    setMicSensitivity(threshold: number) {
      liveKitManager.setMicSensitivity(threshold)
    },
    setSpeakerVolume(volume: number) {
      liveKitManager.setSpeakerVolume(volume)
    },
    setParticipantVolume(identity: string, kind: 'mic' | 'screenShare', percent: number) {
      liveKitManager.setParticipantVolume(identity, kind, percent)
    },
    setParticipantMuted(identity: string, muted: boolean) {
      liveKitManager.setParticipantMuted(identity, muted)
    },
    setScreenShareWatched(identity: string, watched: boolean) {
      liveKitManager.setScreenShareWatched(identity, watched)
    },
    async setVideoQuality(quality: VideoQuality) {
      await liveKitManager.setVideoQuality(quality)
    },
    async setScreenShareQuality(quality: ScreenShareQuality) {
      await liveKitManager.setScreenShareQuality(quality)
    },
    async setScreenShareFps(fps: number) {
      await liveKitManager.setScreenShareFps(fps)
    },
    async setScreenSharePreset(preset: 'text' | 'motion') {
      await liveKitManager.setScreenSharePreset(preset)
    },
    async setScreenShareAudioEnabled(enabled: boolean) {
      await liveKitManager.setScreenShareAudioEnabled(enabled)
    },
  },
})
